/**
 * locationUtils.ts
 *
 * Utility functions and data for location management
 * This file provides base hierarchical location data for Nigerian states, LGAs, and neighborhoods,
 * particularly focused on Lagos and Oyo states.
 *
 * Used in conjunction with locationData.ts which holds the complete location dataset
 * with additional metadata like coordinates, descriptions, and search terms.
 */

// Removed unused import of Coordinates

export type LocationType = "state" | "lga" | "neighborhood";

/**
 * Base location data structure
 * Simpler version compared to the more comprehensive NigerianLocation type in locationData.ts
 */
export interface LocationData {
  id: string;
  name: string;
  type: LocationType;
  parent?: string; // Parent ID (e.g., LGA's parent is State)
  coordinates?: LocalCoordinates; // Add coordinates to LocationData
}

/**
 * Basic coordinates structure
 * Note: The full application generally uses the more detailed Coordinates type from locationData.ts
 */
export interface LocalCoordinates {
  lat: number;
  lng: number;
}

/**
 * Database-driven location interfaces
 */
export interface DatabaseCountry {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
  states: DatabaseState[];
}

export interface DatabaseState {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
  bounds?: {
    north: number;
    south: number;
    west: number;
    east: number;
  };
  center?: {
    lat: number;
    lng: number;
  };
}

const ACTIVE_LOCATIONS_CACHE_MS = 5 * 60 * 1000;
let activeLocationsCache: DatabaseCountry[] | null = null;
let activeLocationsCacheExpiry = 0;
let activeLocationsInFlight: Promise<DatabaseCountry[]> | null = null;

/**
 * Fetch active countries and states from the database
 */
export const fetchActiveLocations = async (): Promise<DatabaseCountry[]> => {
  const now = Date.now();
  if (activeLocationsCache && now < activeLocationsCacheExpiry) {
    return activeLocationsCache;
  }
  if (activeLocationsInFlight) {
    return activeLocationsInFlight;
  }

  activeLocationsInFlight = (async () => {
    try {
      const response = await fetch("/api/locations/active");
      if (!response.ok) {
        throw new Error("Failed to fetch active locations");
      }
      const data = await response.json();
      const countries = (data.countries || []) as DatabaseCountry[];
      activeLocationsCache = countries;
      activeLocationsCacheExpiry = Date.now() + ACTIVE_LOCATIONS_CACHE_MS;
      return countries;
    } catch (error) {
      console.error("Error fetching active locations:", error);
      return [];
    } finally {
      activeLocationsInFlight = null;
    }
  })();

  try {
    return await activeLocationsInFlight;
  } catch {
    return [];
  }
};

/**
 * Get active states for a specific country
 */
export const getActiveStatesForCountry = async (
  countryCode: string,
): Promise<DatabaseState[]> => {
  try {
    const countries = await fetchActiveLocations();
    const country = countries.find((c) => c.code === countryCode);
    return country?.states || [];
  } catch (error) {
    console.error("Error fetching active states for country:", error);
    return [];
  }
};

/**
 * Check if a state is active
 */
export const isStateActive = async (
  stateName: string,
  countryCode: string = "NG",
): Promise<boolean> => {
  try {
    const states = await getActiveStatesForCountry(countryCode);
    return states.some((state) => state.name === stateName && state.is_active);
  } catch (error) {
    console.error("Error checking if state is active:", error);
    return false;
  }
};

/**
 * Get bounds for an active state
 */
export const getStateBounds = async (
  stateName: string,
  countryCode: string = "NG",
): Promise<DatabaseState["bounds"] | null> => {
  try {
    const states = await getActiveStatesForCountry(countryCode);
    const state = states.find((s) => s.name === stateName);
    return state?.bounds || null;
  } catch (error) {
    console.error("Error getting state bounds:", error);
    return null;
  }
};

// Lagos state data
export const LAGOS_STATE: LocationData = {
  id: "lagos_state",
  name: "Lagos",
  type: "state",
};

// Oyo state data
export const OYO_STATE: LocationData = {
  id: "oyo_state",
  name: "Oyo",
  type: "state",
};

/**
 * Complete list of Local Government Areas (LGAs) in Lagos State
 * Total: 20 LGAs
 */
export const LAGOS_LGAS: LocationData[] = [
  { id: "agege_lga", name: "Agege", type: "lga", parent: "lagos_state" },
  {
    id: "ajeromi_ifelodun_lga",
    name: "Ajeromi-Ifelodun",
    type: "lga",
    parent: "lagos_state",
  },
  { id: "alimosho_lga", name: "Alimosho", type: "lga", parent: "lagos_state" },
  {
    id: "amuwo_odofin_lga",
    name: "Amuwo-Odofin",
    type: "lga",
    parent: "lagos_state",
  },
  { id: "apapa_lga", name: "Apapa", type: "lga", parent: "lagos_state" },
  { id: "badagry_lga", name: "Badagry", type: "lga", parent: "lagos_state" },
  { id: "epe_lga", name: "Epe", type: "lga", parent: "lagos_state" },
  { id: "eti_osa_lga", name: "Eti-Osa", type: "lga", parent: "lagos_state" },
  {
    id: "ibeju_lekki_lga",
    name: "Ibeju-Lekki",
    type: "lga",
    parent: "lagos_state",
  },
  {
    id: "ifako_ijaiye_lga",
    name: "Ifako-Ijaiye",
    type: "lga",
    parent: "lagos_state",
  },
  { id: "ikeja_lga", name: "Ikeja", type: "lga", parent: "lagos_state" },
  { id: "ikorodu_lga", name: "Ikorodu", type: "lga", parent: "lagos_state" },
  { id: "kosofe_lga", name: "Kosofe", type: "lga", parent: "lagos_state" },
  {
    id: "lagos_island_lga",
    name: "Lagos Island",
    type: "lga",
    parent: "lagos_state",
  },
  {
    id: "lagos_mainland_lga",
    name: "Lagos Mainland",
    type: "lga",
    parent: "lagos_state",
  },
  { id: "mushin_lga", name: "Mushin", type: "lga", parent: "lagos_state" },
  { id: "ojo_lga", name: "Ojo", type: "lga", parent: "lagos_state" },
  {
    id: "oshodi_isolo_lga",
    name: "Oshodi-Isolo",
    type: "lga",
    parent: "lagos_state",
  },
  { id: "shomolu_lga", name: "Shomolu", type: "lga", parent: "lagos_state" },
  { id: "surulere_lga", name: "Surulere", type: "lga", parent: "lagos_state" },
];

/**
 * Complete list of Local Government Areas (LGAs) in Oyo State
 * Total: 33 LGAs
 */
export const OYO_LGAS: LocationData[] = [
  { id: "afijio_lga", name: "Afijio", type: "lga", parent: "oyo_state" },
  { id: "akinyele_lga", name: "Akinyele", type: "lga", parent: "oyo_state" },
  { id: "atiba_lga", name: "Atiba", type: "lga", parent: "oyo_state" },
  { id: "atisbo_lga", name: "Atisbo", type: "lga", parent: "oyo_state" },
  { id: "egbeda_lga", name: "Egbeda", type: "lga", parent: "oyo_state" },
  {
    id: "ibadan_north_lga",
    name: "Ibadan North",
    type: "lga",
    parent: "oyo_state",
  },
  {
    id: "ibadan_north_east_lga",
    name: "Ibadan North East",
    type: "lga",
    parent: "oyo_state",
  },
  {
    id: "ibadan_north_west_lga",
    name: "Ibadan North West",
    type: "lga",
    parent: "oyo_state",
  },
  {
    id: "ibadan_south_east_lga",
    name: "Ibadan South East",
    type: "lga",
    parent: "oyo_state",
  },
  {
    id: "ibadan_south_west_lga",
    name: "Ibadan South West",
    type: "lga",
    parent: "oyo_state",
  },
  {
    id: "ibarapa_central_lga",
    name: "Ibarapa Central",
    type: "lga",
    parent: "oyo_state",
  },
  {
    id: "ibarapa_east_lga",
    name: "Ibarapa East",
    type: "lga",
    parent: "oyo_state",
  },
  {
    id: "ibarapa_north_lga",
    name: "Ibarapa North",
    type: "lga",
    parent: "oyo_state",
  },
  { id: "ido_lga", name: "Ido", type: "lga", parent: "oyo_state" },
  { id: "irepo_lga", name: "Irepo", type: "lga", parent: "oyo_state" },
  { id: "iseyin_lga", name: "Iseyin", type: "lga", parent: "oyo_state" },
  { id: "itesiwaju_lga", name: "Itesiwaju", type: "lga", parent: "oyo_state" },
  { id: "iwajowa_lga", name: "Iwajowa", type: "lga", parent: "oyo_state" },
  { id: "kajola_lga", name: "Kajola", type: "lga", parent: "oyo_state" },
  { id: "lagelu_lga", name: "Lagelu", type: "lga", parent: "oyo_state" },
  {
    id: "ogbomosho_north_lga",
    name: "Ogbomosho North",
    type: "lga",
    parent: "oyo_state",
  },
  {
    id: "ogbomosho_south_lga",
    name: "Ogbomosho South",
    type: "lga",
    parent: "oyo_state",
  },
  { id: "ogo_oluwa_lga", name: "Ogo Oluwa", type: "lga", parent: "oyo_state" },
  {
    id: "olorunsogo_lga",
    name: "Olorunsogo",
    type: "lga",
    parent: "oyo_state",
  },
  { id: "oluyole_lga", name: "Oluyole", type: "lga", parent: "oyo_state" },
  { id: "ona_ara_lga", name: "Ona Ara", type: "lga", parent: "oyo_state" },
  { id: "orelope_lga", name: "Orelope", type: "lga", parent: "oyo_state" },
  { id: "ori_ire_lga", name: "Ori Ire", type: "lga", parent: "oyo_state" },
  { id: "oyo_east_lga", name: "Oyo East", type: "lga", parent: "oyo_state" },
  { id: "oyo_west_lga", name: "Oyo West", type: "lga", parent: "oyo_state" },
  { id: "saki_east_lga", name: "Saki East", type: "lga", parent: "oyo_state" },
  { id: "saki_west_lga", name: "Saki West", type: "lga", parent: "oyo_state" },
  {
    id: "surulere_oyo_lga",
    name: "Surulere",
    type: "lga",
    parent: "oyo_state",
  },
];

/**
 * Neighborhoods in Lagos organized by LGA
 * This is reference data for the complete data found in locationData.ts
 * This can be used for quick lookups of neighborhood names and their LGA associations
 */
export const LAGOS_NEIGHBORHOODS: LocationData[] = [
  // Agege LGA
  { id: "agege", name: "Agege", type: "neighborhood", parent: "agege_lga" },
  { id: "dopemu", name: "Dopemu", type: "neighborhood", parent: "agege_lga" },
  {
    id: "isale_odo",
    name: "Isale Odo",
    type: "neighborhood",
    parent: "agege_lga",
  },
  { id: "iloro", name: "Iloro", type: "neighborhood", parent: "agege_lga" },
  { id: "oniwaya", name: "Oniwaya", type: "neighborhood", parent: "agege_lga" },
  {
    id: "papa_uku",
    name: "Papa-Uku",
    type: "neighborhood",
    parent: "agege_lga",
  },
  {
    id: "agbotikuyo",
    name: "Agbotikuyo",
    type: "neighborhood",
    parent: "agege_lga",
  },
  { id: "keke", name: "Keke", type: "neighborhood", parent: "agege_lga" },
  { id: "darocha", name: "Darocha", type: "neighborhood", parent: "agege_lga" },
  {
    id: "tabon_tabon",
    name: "Tabon-Tabon",
    type: "neighborhood",
    parent: "agege_lga",
  },
  {
    id: "orile_agege",
    name: "Orile Agege",
    type: "neighborhood",
    parent: "agege_lga",
  },
  { id: "ile_epo", name: "Ile-Epo", type: "neighborhood", parent: "agege_lga" },
  { id: "oko_oba", name: "Oko-Oba", type: "neighborhood", parent: "agege_lga" },
  { id: "markaz", name: "Markaz", type: "neighborhood", parent: "agege_lga" },
  {
    id: "papa_ashafa",
    name: "Papa Ashafa",
    type: "neighborhood",
    parent: "agege_lga",
  },

  // Ajeromi-Ifelodun LGA
  {
    id: "ajegunle",
    name: "Ajegunle",
    type: "neighborhood",
    parent: "ajeromi_ifelodun_lga",
  },
  {
    id: "amukoko",
    name: "Amukoko",
    type: "neighborhood",
    parent: "ajeromi_ifelodun_lga",
  },
  {
    id: "awodi_ora",
    name: "Awodi-Ora",
    type: "neighborhood",
    parent: "ajeromi_ifelodun_lga",
  },
  {
    id: "tolu",
    name: "Tolu",
    type: "neighborhood",
    parent: "ajeromi_ifelodun_lga",
  },
  {
    id: "alaba_oro",
    name: "Alaba Oro",
    type: "neighborhood",
    parent: "ajeromi_ifelodun_lga",
  },
  {
    id: "layeni",
    name: "Layeni",
    type: "neighborhood",
    parent: "ajeromi_ifelodun_lga",
  },
  {
    id: "temidire",
    name: "Temidire",
    type: "neighborhood",
    parent: "ajeromi_ifelodun_lga",
  },
  {
    id: "ojo_road",
    name: "Ojo Road",
    type: "neighborhood",
    parent: "ajeromi_ifelodun_lga",
  },
  {
    id: "mosafejo",
    name: "Mosafejo",
    type: "neighborhood",
    parent: "ajeromi_ifelodun_lga",
  },

  // Alimosho LGA
  {
    id: "ikotun",
    name: "Ikotun",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  {
    id: "egbeda",
    name: "Egbeda",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  {
    id: "akowonjo",
    name: "Akowonjo",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  {
    id: "alimosho",
    name: "Alimosho",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  { id: "idimu", name: "Idimu", type: "neighborhood", parent: "alimosho_lga" },
  {
    id: "isheri_olofin",
    name: "Isheri Olofin",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  {
    id: "akesan",
    name: "Akesan",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  {
    id: "ijegun",
    name: "Ijegun",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  { id: "egbe", name: "Egbe", type: "neighborhood", parent: "alimosho_lga" },
  { id: "agodo", name: "Agodo", type: "neighborhood", parent: "alimosho_lga" },
  {
    id: "igando",
    name: "Igando",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  { id: "egan", name: "Egan", type: "neighborhood", parent: "alimosho_lga" },
  { id: "ipaja", name: "Ipaja", type: "neighborhood", parent: "alimosho_lga" },
  { id: "ayobo", name: "Ayobo", type: "neighborhood", parent: "alimosho_lga" },
  { id: "ijon", name: "Ijon", type: "neighborhood", parent: "alimosho_lga" },
  {
    id: "oke_odo",
    name: "Oke-Odo",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  {
    id: "abule_egba",
    name: "Abule-Egba",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  { id: "aboru", name: "Aboru", type: "neighborhood", parent: "alimosho_lga" },
  {
    id: "meiran",
    name: "Meiran",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  {
    id: "alagbado",
    name: "Alagbado",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  {
    id: "ipaja_ayobo",
    name: "Ipaja-Ayobo",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  {
    id: "baruwa",
    name: "Baruwa",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  {
    id: "command",
    name: "Command",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  {
    id: "gowon_estate",
    name: "Gowon Estate",
    type: "neighborhood",
    parent: "alimosho_lga",
  },
  { id: "ikola", name: "Ikola", type: "neighborhood", parent: "alimosho_lga" },

  // Amuwo-Odofin LGA
  // { id: 'festac', name: 'Festac Town', type: 'neighborhood', parent: 'amuwo_odofin_lga' },
  {
    id: "mile_2",
    name: "Mile 2",
    type: "neighborhood",
    parent: "amuwo_odofin_lga",
  },
  {
    id: "kirikiri",
    name: "Kirikiri",
    type: "neighborhood",
    parent: "amuwo_odofin_lga",
  },
  {
    id: "amuwo",
    name: "Amuwo",
    type: "neighborhood",
    parent: "amuwo_odofin_lga",
  },
  {
    id: "amuwo_ijegun",
    name: "Ijegun",
    type: "neighborhood",
    parent: "amuwo_odofin_lga",
  },
  {
    id: "satellite_town",
    name: "Satellite Town",
    type: "neighborhood",
    parent: "amuwo_odofin_lga",
  },
  {
    id: "irede",
    name: "Irede",
    type: "neighborhood",
    parent: "amuwo_odofin_lga",
  },
  {
    id: "ibeshe",
    name: "Ibeshe",
    type: "neighborhood",
    parent: "amuwo_odofin_lga",
  },
  {
    id: "igbologun",
    name: "Igbologun",
    type: "neighborhood",
    parent: "amuwo_odofin_lga",
  },
  {
    id: "abule_ado",
    name: "Abule Ado",
    type: "neighborhood",
    parent: "amuwo_odofin_lga",
  },
  {
    id: "festac_town",
    name: "Festac Town",
    type: "neighborhood",
    parent: "amuwo_odofin_lga",
  },

  // Apapa LGA
  { id: "apapa", name: "Apapa", type: "neighborhood", parent: "apapa_lga" },
  {
    id: "ijora_oloye",
    name: "Ijora-Oloye",
    type: "neighborhood",
    parent: "apapa_lga",
  },
  { id: "olodan", name: "Olodan", type: "neighborhood", parent: "apapa_lga" },
  { id: "gaskiya", name: "Gaskiya", type: "neighborhood", parent: "apapa_lga" },
  {
    id: "afolabi_alasia",
    name: "Afolabi Alasia",
    type: "neighborhood",
    parent: "apapa_lga",
  },
  {
    id: "malu_road",
    name: "Malu Road",
    type: "neighborhood",
    parent: "apapa_lga",
  },
  { id: "sari", name: "Sari", type: "neighborhood", parent: "apapa_lga" },

  // Badagry LGA
  {
    id: "badagry",
    name: "Badagry",
    type: "neighborhood",
    parent: "badagry_lga",
  },
  {
    id: "posukoh",
    name: "Posukoh",
    type: "neighborhood",
    parent: "badagry_lga",
  },
  {
    id: "awhanjigoh",
    name: "Awhanjigoh",
    type: "neighborhood",
    parent: "badagry_lga",
  },
  {
    id: "ibereko",
    name: "Ibereko",
    type: "neighborhood",
    parent: "badagry_lga",
  },
  {
    id: "keta_east",
    name: "Keta-East",
    type: "neighborhood",
    parent: "badagry_lga",
  },
  {
    id: "iworo_gbanko",
    name: "Iworo Gbanko",
    type: "neighborhood",
    parent: "badagry_lga",
  },
  { id: "ajido", name: "Ajido", type: "neighborhood", parent: "badagry_lga" },
  {
    id: "ilogbo_araromi",
    name: "Ilogbo-Araromi",
    type: "neighborhood",
    parent: "badagry_lga",
  },
  { id: "ikoga", name: "Ikoga", type: "neighborhood", parent: "badagry_lga" },
  { id: "ajara", name: "Ajara", type: "neighborhood", parent: "badagry_lga" },
  {
    id: "iya_afin",
    name: "Iya-Afin",
    type: "neighborhood",
    parent: "badagry_lga",
  },
  { id: "topo", name: "Topo", type: "neighborhood", parent: "badagry_lga" },
  { id: "iworo", name: "Iworo", type: "neighborhood", parent: "badagry_lga" },
  {
    id: "aradagun",
    name: "Aradagun",
    type: "neighborhood",
    parent: "badagry_lga",
  },

  // Epe LGA
  { id: "epe", name: "Epe", type: "neighborhood", parent: "epe_lga" },
  { id: "etita", name: "Etita", type: "neighborhood", parent: "epe_lga" },
  { id: "ebode", name: "Ebode", type: "neighborhood", parent: "epe_lga" },
  { id: "lagbade", name: "Lagbade", type: "neighborhood", parent: "epe_lga" },
  { id: "popo_oba", name: "Popo-Oba", type: "neighborhood", parent: "epe_lga" },
  {
    id: "oke_balogun",
    name: "Oke-Balogun",
    type: "neighborhood",
    parent: "epe_lga",
  },
  {
    id: "ajaganabe",
    name: "Ajaganabe",
    type: "neighborhood",
    parent: "epe_lga",
  },
  { id: "ise", name: "Ise", type: "neighborhood", parent: "epe_lga" },
  { id: "igbogun", name: "Igbogun", type: "neighborhood", parent: "epe_lga" },
  { id: "oriba", name: "Oriba", type: "neighborhood", parent: "epe_lga" },
  { id: "ladaba", name: "Ladaba", type: "neighborhood", parent: "epe_lga" },
  { id: "abomiti", name: "Abomiti", type: "neighborhood", parent: "epe_lga" },
  { id: "agbowa", name: "Agbowa", type: "neighborhood", parent: "epe_lga" },
  { id: "ikosi_epe", name: "Ikosi", type: "neighborhood", parent: "epe_lga" },
  { id: "ago_owu", name: "Ago Owu", type: "neighborhood", parent: "epe_lga" },
  { id: "orugbo", name: "Orugbo", type: "neighborhood", parent: "epe_lga" },
  { id: "ilara", name: "Ilara", type: "neighborhood", parent: "epe_lga" },
  { id: "ibonwon", name: "Ibonwon", type: "neighborhood", parent: "epe_lga" },
  {
    id: "odoragunsin",
    name: "Odoragunsin",
    type: "neighborhood",
    parent: "epe_lga",
  },
  { id: "poka", name: "Poka", type: "neighborhood", parent: "epe_lga" },
  { id: "odomola", name: "Odomola", type: "neighborhood", parent: "epe_lga" },
  { id: "ejirin", name: "Ejirin", type: "neighborhood", parent: "epe_lga" },
  { id: "itoikin", name: "Itoikin", type: "neighborhood", parent: "epe_lga" },
  { id: "epe_town", name: "Epe Town", type: "neighborhood", parent: "epe_lga" },
  {
    id: "agbowa_ikosi",
    name: "Agbowa-Ikosi",
    type: "neighborhood",
    parent: "epe_lga",
  },
  { id: "noforija", name: "Noforija", type: "neighborhood", parent: "epe_lga" },

  // Eti-Osa LGA
  {
    id: "victoria_island",
    name: "Victoria Island",
    type: "neighborhood",
    parent: "eti_osa_lga",
  },
  {
    id: "ilasan_housing_estate",
    name: "Ilasan Housing Estate",
    type: "neighborhood",
    parent: "eti_osa_lga",
  },
  { id: "lekki", name: "Lekki", type: "neighborhood", parent: "eti_osa_lga" },
  {
    id: "lekki_phase_1",
    name: "Lekki Phase 1",
    type: "neighborhood",
    parent: "eti_osa_lga",
  },
  {
    id: "lekki_phase_2",
    name: "Lekki Phase 2",
    type: "neighborhood",
    parent: "eti_osa_lga",
  },
  { id: "ikate", name: "Ikate", type: "neighborhood", parent: "eti_osa_lga" },
  { id: "ilado", name: "Ilado", type: "neighborhood", parent: "eti_osa_lga" },
  { id: "ajah", name: "Ajah", type: "neighborhood", parent: "eti_osa_lga" },
  {
    id: "sangotedo",
    name: "Sangotedo",
    type: "neighborhood",
    parent: "eti_osa_lga",
  },
  { id: "ado", name: "Ado", type: "neighborhood", parent: "eti_osa_lga" },
  {
    id: "langbasa",
    name: "Langbasa",
    type: "neighborhood",
    parent: "eti_osa_lga",
  },
  { id: "badore", name: "Badore", type: "neighborhood", parent: "eti_osa_lga" },
  { id: "ikoyi", name: "Ikoyi", type: "neighborhood", parent: "eti_osa_lga" },
  {
    id: "obalende",
    name: "Obalende",
    type: "neighborhood",
    parent: "eti_osa_lga",
  },
  {
    id: "chevron",
    name: "Chevron",
    type: "neighborhood",
    parent: "eti_osa_lga",
  },
  {
    id: "ikate_elegushi",
    name: "Ikate Elegushi",
    type: "neighborhood",
    parent: "eti_osa_lga",
  },
  {
    id: "osapa_london",
    name: "Osapa London",
    type: "neighborhood",
    parent: "eti_osa_lga",
  },
  {
    id: "banana_island",
    name: "Banana Island",
    type: "neighborhood",
    parent: "eti_osa_lga",
  },

  // Ibeju-Lekki LGA
  {
    id: "ibeju",
    name: "Ibeju",
    type: "neighborhood",
    parent: "ibeju_lekki_lga",
  },
  {
    id: "orimedu",
    name: "Orimedu",
    type: "neighborhood",
    parent: "ibeju_lekki_lga",
  },
  {
    id: "iwerekun",
    name: "Iwerekun",
    type: "neighborhood",
    parent: "ibeju_lekki_lga",
  },
  {
    id: "lekki_ibeju",
    name: "Lekki",
    type: "neighborhood",
    parent: "ibeju_lekki_lga",
  },
  {
    id: "siriwon",
    name: "Siriwon",
    type: "neighborhood",
    parent: "ibeju_lekki_lga",
  },
  {
    id: "igbekodo",
    name: "Igbekodo",
    type: "neighborhood",
    parent: "ibeju_lekki_lga",
  },
  {
    id: "lakowe",
    name: "Lakowe",
    type: "neighborhood",
    parent: "ibeju_lekki_lga",
  },
  {
    id: "awoyaya",
    name: "Awoyaya",
    type: "neighborhood",
    parent: "ibeju_lekki_lga",
  },
  {
    id: "bogije",
    name: "Bogije",
    type: "neighborhood",
    parent: "ibeju_lekki_lga",
  },
  {
    id: "elemoro",
    name: "Elemoro",
    type: "neighborhood",
    parent: "ibeju_lekki_lga",
  },
  {
    id: "akodo",
    name: "Akodo",
    type: "neighborhood",
    parent: "ibeju_lekki_lga",
  },

  // Ifako-Ijaiye LGA
  {
    id: "ifako",
    name: "Ifako",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "ijaye",
    name: "Ijaye",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "old_ifako",
    name: "Old Ifako",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "karaole",
    name: "Karaole",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "new_ifako",
    name: "New Ifako",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "oyemekun",
    name: "Oyemekun",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "fagba",
    name: "Fagba",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "akute_road",
    name: "Akute Road",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "iju_isaga",
    name: "Iju Isaga",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "iju_obawole",
    name: "Iju-Obawole",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "pamada",
    name: "Pamada",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "abule_egba_ifako",
    name: "Abule-Egba",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "ijaiye",
    name: "Ijaiye",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "ojokoro",
    name: "Ojokoro",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "agbado",
    name: "Agbado",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "kollington",
    name: "Kollington",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "alakuko",
    name: "Alakuko",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "ajegunle_ifako",
    name: "Ajegunle",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "akinde",
    name: "Akinde",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },
  {
    id: "animashaun",
    name: "Animashaun",
    type: "neighborhood",
    parent: "ifako_ijaiye_lga",
  },

  // Ikeja LGA
  { id: "ikeja", name: "Ikeja", type: "neighborhood", parent: "ikeja_lga" },
  {
    id: "anifowoshe",
    name: "Anifowoshe",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  { id: "ojodu", name: "Ojodu", type: "neighborhood", parent: "ikeja_lga" },
  {
    id: "ojodu_berger",
    name: "Ojodu Berger",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  {
    id: "agidingbi",
    name: "Agidingbi",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  { id: "omole", name: "Omole", type: "neighborhood", parent: "ikeja_lga" },
  { id: "alausa", name: "Alausa", type: "neighborhood", parent: "ikeja_lga" },
  { id: "oregun", name: "Oregun", type: "neighborhood", parent: "ikeja_lga" },
  {
    id: "olusosun",
    name: "Olusosun",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  {
    id: "onipetesi",
    name: "Onipetesi",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  {
    id: "onilekere",
    name: "Onilekere",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  { id: "ipodo", name: "Ipodo", type: "neighborhood", parent: "ikeja_lga" },
  {
    id: "seriki_aro",
    name: "Seriki Aro",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  {
    id: "adekunle_village",
    name: "Adekunle Village",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  {
    id: "adeniyi_jones",
    name: "Adeniyi Jones",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  { id: "ogba", name: "Ogba", type: "neighborhood", parent: "ikeja_lga" },
  { id: "oke_ira", name: "Oke-Ira", type: "neighborhood", parent: "ikeja_lga" },
  {
    id: "aguda_ikeja",
    name: "Aguda",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  {
    id: "onigbongbo",
    name: "Onigbongbo",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  {
    id: "military_cantonment",
    name: "Military Cantonment",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  {
    id: "ikeja_gra",
    name: "Ikeja GRA",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  {
    id: "police_barracks",
    name: "Police Barracks",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  { id: "wasimi", name: "Wasimi", type: "neighborhood", parent: "ikeja_lga" },
  { id: "opebi", name: "Opebi", type: "neighborhood", parent: "ikeja_lga" },
  { id: "allen", name: "Allen", type: "neighborhood", parent: "ikeja_lga" },
  {
    id: "maryland",
    name: "Maryland",
    type: "neighborhood",
    parent: "ikeja_lga",
  },
  {
    id: "computer_village",
    name: "Computer Village",
    type: "neighborhood",
    parent: "ikeja_lga",
  },

  // Ikorodu LGA
  {
    id: "ikorodu",
    name: "Ikorodu",
    type: "neighborhood",
    parent: "ikorodu_lga",
  },
  { id: "isele", name: "Isele", type: "neighborhood", parent: "ikorodu_lga" },
  { id: "aga", name: "Aga", type: "neighborhood", parent: "ikorodu_lga" },
  { id: "ijimu", name: "Ijimu", type: "neighborhood", parent: "ikorodu_lga" },
  {
    id: "ipakodo",
    name: "Ipakodo",
    type: "neighborhood",
    parent: "ikorodu_lga",
  },
  { id: "imota", name: "Imota", type: "neighborhood", parent: "ikorodu_lga" },
  { id: "isiu", name: "Isiu", type: "neighborhood", parent: "ikorodu_lga" },
  {
    id: "igbogbo",
    name: "Igbogbo",
    type: "neighborhood",
    parent: "ikorodu_lga",
  },
  {
    id: "baiyeku",
    name: "Baiyeku",
    type: "neighborhood",
    parent: "ikorodu_lga",
  },
  { id: "oreta", name: "Oreta", type: "neighborhood", parent: "ikorodu_lga" },
  { id: "ijede", name: "Ijede", type: "neighborhood", parent: "ikorodu_lga" },
  { id: "agura", name: "Agura", type: "neighborhood", parent: "ikorodu_lga" },
  { id: "iponmi", name: "Iponmi", type: "neighborhood", parent: "ikorodu_lga" },
  {
    id: "odogunyan",
    name: "Odogunyan",
    type: "neighborhood",
    parent: "ikorodu_lga",
  },
  {
    id: "erikorodu",
    name: "Erikorodu",
    type: "neighborhood",
    parent: "ikorodu_lga",
  },
  { id: "agbala", name: "Agbala", type: "neighborhood", parent: "ikorodu_lga" },
  {
    id: "olorunda",
    name: "Olorunda",
    type: "neighborhood",
    parent: "ikorodu_lga",
  },
  { id: "igbaga", name: "Igbaga", type: "neighborhood", parent: "ikorodu_lga" },
  { id: "bayeku", name: "Bayeku", type: "neighborhood", parent: "ikorodu_lga" },
  { id: "agbowa", name: "Agbowa", type: "neighborhood", parent: "ikorodu_lga" },

  // Kosofe LGA
  { id: "kosofe", name: "Kosofe", type: "neighborhood", parent: "kosofe_lga" },
  {
    id: "oworonshoki",
    name: "Oworonshoki",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  {
    id: "ifako_kosofe",
    name: "Ifako",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  { id: "soluyi", name: "Soluyi", type: "neighborhood", parent: "kosofe_lga" },
  {
    id: "anthony",
    name: "Anthony",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  { id: "mende", name: "Mende", type: "neighborhood", parent: "kosofe_lga" },
  {
    id: "maryland_kosofe",
    name: "Maryland",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  { id: "ojota", name: "Ojota", type: "neighborhood", parent: "kosofe_lga" },
  { id: "ogudu", name: "Ogudu", type: "neighborhood", parent: "kosofe_lga" },
  { id: "ketu", name: "Ketu", type: "neighborhood", parent: "kosofe_lga" },
  {
    id: "alapere",
    name: "Alapere",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  { id: "agidi", name: "Agidi", type: "neighborhood", parent: "kosofe_lga" },
  {
    id: "orisigun",
    name: "Orisigun",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  {
    id: "ajelogo",
    name: "Ajelogo",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  {
    id: "akanimodo",
    name: "Akanimodo",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  { id: "ikosi", name: "Ikosi", type: "neighborhood", parent: "kosofe_lga" },
  {
    id: "mile_12",
    name: "Mile 12",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  {
    id: "agiliti",
    name: "Agiliti",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  { id: "maidan", name: "Maidan", type: "neighborhood", parent: "kosofe_lga" },
  {
    id: "isheri_olowo_ira",
    name: "Isheri-Olowo-Ira",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  {
    id: "shangisha",
    name: "Shangisha",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  { id: "magodo", name: "Magodo", type: "neighborhood", parent: "kosofe_lga" },
  { id: "agboyi", name: "Agboyi", type: "neighborhood", parent: "kosofe_lga" },
  {
    id: "owode_onirin",
    name: "Owode Onirin",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  {
    id: "ajegunle_kosofe",
    name: "Ajegunle",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  {
    id: "odo_ogun",
    name: "Odo-Ogun",
    type: "neighborhood",
    parent: "kosofe_lga",
  },
  {
    id: "gbagada",
    name: "Gbagada",
    type: "neighborhood",
    parent: "kosofe_lga",
  },

  // Lagos Island LGA
  {
    id: "lagos_island",
    name: "Lagos Island",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "olowogbowo",
    name: "Olowogbowo",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "elegbata",
    name: "Elegbata",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "oluwole",
    name: "Oluwole",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "idumota",
    name: "Idumota",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  { id: "oke", name: "Oke", type: "neighborhood", parent: "lagos_island_lga" },
  {
    id: "oju_oto",
    name: "Oju-Oto",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "oko_awo",
    name: "Oko-Awo",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "agarawu",
    name: "Agarawu",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "obadina",
    name: "Obadina",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "iduntafa",
    name: "Iduntafa",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "ilupesi",
    name: "Ilupesi",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "isale_agbede",
    name: "Isale-Agbede",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "olosun",
    name: "Olosun",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "olushi",
    name: "Olushi",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "kakawa",
    name: "Kakawa",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "popo_aguda",
    name: "Popo-Aguda",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "anikantamo",
    name: "Anikantamo",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "oko_faji",
    name: "Oko-Faji",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "eiyekole",
    name: "Eiyekole",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "onikan",
    name: "Onikan",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "sandgrouse",
    name: "Sandgrouse",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "epetedo",
    name: "Epetedo",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "lafiaji",
    name: "Lafiaji",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },
  {
    id: "ebute",
    name: "Ebute",
    type: "neighborhood",
    parent: "lagos_island_lga",
  },

  // Lagos Mainland LGA
  {
    id: "lagos_mainland",
    name: "Lagos Mainland",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "otto",
    name: "Otto",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "iddo",
    name: "Iddo",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "olaleye_village",
    name: "Olaleye Village",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "maroko",
    name: "Maroko",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "ebute_metta",
    name: "Ebute Metta",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "oyingbo_market",
    name: "Oyingbo Market",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "glover",
    name: "Glover",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "oko_baba",
    name: "Oko-Baba",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "oyadiran_estate",
    name: "Oyadiran Estate",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "abule_oja",
    name: "Abule-Oja",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "alagomeji",
    name: "Alagomeji",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "iwaya",
    name: "Iwaya",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "yaba",
    name: "Yaba",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "igbobi",
    name: "Igbobi",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },
  {
    id: "akoka",
    name: "Akoka",
    type: "neighborhood",
    parent: "lagos_mainland_lga",
  },

  // Mushin LGA
  { id: "mushin", name: "Mushin", type: "neighborhood", parent: "mushin_lga" },
  {
    id: "alakara",
    name: "Alakara",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  {
    id: "idi_oro",
    name: "Idi-Oro",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  {
    id: "odi_olowu",
    name: "Odi-Olowu",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  {
    id: "babalosa",
    name: "Babalosa",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  {
    id: "ojuwoye",
    name: "Ojuwoye",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  {
    id: "ilupeju",
    name: "Ilupeju",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  {
    id: "olateju",
    name: "Olateju",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  { id: "kayode", name: "Kayode", type: "neighborhood", parent: "mushin_lga" },
  { id: "fadeyi", name: "Fadeyi", type: "neighborhood", parent: "mushin_lga" },
  {
    id: "atewolara",
    name: "Atewolara",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  {
    id: "papa_ajao",
    name: "Papa-Ajao",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  {
    id: "ilasamaja",
    name: "Ilasamaja",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  {
    id: "idi_araba",
    name: "Idi-Araba",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  { id: "itire", name: "Itire", type: "neighborhood", parent: "mushin_lga" },
  {
    id: "ilupeju_industrial_estate",
    name: "Ilupeju Industrial Estate",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  {
    id: "palm_avenue",
    name: "Palm Avenue",
    type: "neighborhood",
    parent: "mushin_lga",
  },
  { id: "ladipo", name: "Ladipo", type: "neighborhood", parent: "mushin_lga" },

  // Ojo LGA
  { id: "ojo", name: "Ojo", type: "neighborhood", parent: "ojo_lga" },
  {
    id: "okokomaiko",
    name: "Okokomaiko",
    type: "neighborhood",
    parent: "ojo_lga",
  },
  {
    id: "ajangbadi",
    name: "Ajangbadi",
    type: "neighborhood",
    parent: "ojo_lga",
  },
  { id: "ijanikin", name: "Ijanikin", type: "neighborhood", parent: "ojo_lga" },
  { id: "iba", name: "Iba", type: "neighborhood", parent: "ojo_lga" },
  { id: "irewe", name: "Irewe", type: "neighborhood", parent: "ojo_lga" },
  { id: "tafi", name: "Tafi", type: "neighborhood", parent: "ojo_lga" },
  { id: "etegbin", name: "Etegbin", type: "neighborhood", parent: "ojo_lga" },
  { id: "idoluwo", name: "Idoluwo", type: "neighborhood", parent: "ojo_lga" },
  { id: "sabo_ojo", name: "Sabo", type: "neighborhood", parent: "ojo_lga" },
  {
    id: "ojo_barracks",
    name: "Ojo Barracks",
    type: "neighborhood",
    parent: "ojo_lga",
  },

  // Oshodi-Isolo LGA
  {
    id: "oshodi",
    name: "Oshodi",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "bolade",
    name: "Bolade",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "orile_oshodi",
    name: "Orile-Oshodi",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "isolo",
    name: "Isolo",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "ajao_estate",
    name: "Ajao Estate",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "mafoluku",
    name: "Mafoluku",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "sogunle",
    name: "Sogunle",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "alasia",
    name: "Alasia",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "okota",
    name: "Okota",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "ishagatedo",
    name: "Ishagatedo",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "oke_afa",
    name: "Oke-Afa",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "ejigbo",
    name: "Ejigbo",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "shogunle",
    name: "Shogunle",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },
  {
    id: "oshodi_market",
    name: "Oshodi Market",
    type: "neighborhood",
    parent: "oshodi_isolo_lga",
  },

  // Shomolu LGA
  {
    id: "shomolu",
    name: "Shomolu",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  {
    id: "onipanu",
    name: "Onipanu",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  {
    id: "palmgrove",
    name: "Palmgrove",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  {
    id: "ijebutedo",
    name: "Ijebutedo",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  { id: "alade", name: "Alade", type: "neighborhood", parent: "shomolu_lga" },
  {
    id: "bajulaiye",
    name: "Bajulaiye",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  {
    id: "mafowoku",
    name: "Mafowoku",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  { id: "pedro", name: "Pedro", type: "neighborhood", parent: "shomolu_lga" },
  {
    id: "lad_lak",
    name: "Lad-Lak",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  { id: "bariga", name: "Bariga", type: "neighborhood", parent: "shomolu_lga" },
  { id: "ilaje", name: "Ilaje", type: "neighborhood", parent: "shomolu_lga" },
  {
    id: "akoka_shomolu",
    name: "Akoka",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  {
    id: "igbobi_shomolu",
    name: "Igbobi",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  {
    id: "fadeyi_shomolu",
    name: "Fadeyi",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  {
    id: "fola_agoro",
    name: "Fola Agoro",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  {
    id: "obanikoro",
    name: "Obanikoro",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  {
    id: "apelehin",
    name: "Apelehin",
    type: "neighborhood",
    parent: "shomolu_lga",
  },
  {
    id: "abule_okuta",
    name: "Abule-Okuta",
    type: "neighborhood",
    parent: "shomolu_lga",
  },

  // Surulere LGA
  {
    id: "surulere",
    name: "Surulere",
    type: "neighborhood",
    parent: "surulere_lga",
  },
  { id: "orile", name: "Orile", type: "neighborhood", parent: "surulere_lga" },
  { id: "aguda", name: "Aguda", type: "neighborhood", parent: "surulere_lga" },
  {
    id: "ijeshatedo",
    name: "Ijeshatedo",
    type: "neighborhood",
    parent: "surulere_lga",
  },
  {
    id: "akinhan",
    name: "Akinhan",
    type: "neighborhood",
    parent: "surulere_lga",
  },
  {
    id: "adeniran_ogunsanya",
    name: "Adeniran Ogunsanya",
    type: "neighborhood",
    parent: "surulere_lga",
  },
  { id: "alaka", name: "Alaka", type: "neighborhood", parent: "surulere_lga" },
];

/**
 * Neighborhoods in Oyo organized by LGA
 * This is reference data for the complete data found in locationData.ts
 * This can be used for quick lookups of neighborhood names and their LGA associations
 */
export const OYO_NEIGHBORHOODS: LocationData[] = [
  // Ibadan North LGA
  {
    id: "agbowo",
    name: "Agbowo",
    type: "neighborhood",
    parent: "ibadan_north_lga",
  },
  {
    id: "bashorun",
    name: "Bashorun",
    type: "neighborhood",
    parent: "ibadan_north_lga",
  },
  {
    id: "bodija",
    name: "Bodija",
    type: "neighborhood",
    parent: "ibadan_north_lga",
  },
  {
    id: "mokola",
    name: "Mokola",
    type: "neighborhood",
    parent: "ibadan_north_lga",
  },
  {
    id: "samonda",
    name: "Samonda",
    type: "neighborhood",
    parent: "ibadan_north_lga",
  },
  {
    id: "sango",
    name: "Sango",
    type: "neighborhood",
    parent: "ibadan_north_lga",
  },
  {
    id: "yemetu",
    name: "Yemetu",
    type: "neighborhood",
    parent: "ibadan_north_lga",
  },

  // Ibadan North East LGA
  {
    id: "agodi",
    name: "Agodi",
    type: "neighborhood",
    parent: "ibadan_north_east_lga",
  },
  {
    id: "gate",
    name: "Gate",
    type: "neighborhood",
    parent: "ibadan_north_east_lga",
  },
  {
    id: "iwo_road",
    name: "Iwo Road",
    type: "neighborhood",
    parent: "ibadan_north_east_lga",
  },

  // Ibadan North West LGA
  {
    id: "oke_ado",
    name: "Oke Ado",
    type: "neighborhood",
    parent: "ibadan_north_west_lga",
  },
  {
    id: "dugbe",
    name: "Dugbe",
    type: "neighborhood",
    parent: "ibadan_north_west_lga",
  },
  {
    id: "onireke",
    name: "Onireke",
    type: "neighborhood",
    parent: "ibadan_north_west_lga",
  },

  // Ibadan South East LGA
  {
    id: "idi_arere",
    name: "Idi Arere",
    type: "neighborhood",
    parent: "ibadan_south_east_lga",
  },
  {
    id: "iwo_road",
    name: "Iwo Road",
    type: "neighborhood",
    parent: "ibadan_south_east_lga",
  },
  {
    id: "mapo",
    name: "Mapo",
    type: "neighborhood",
    parent: "ibadan_south_east_lga",
  },
  {
    id: "orita_aperin",
    name: "Orita Aperin",
    type: "neighborhood",
    parent: "ibadan_south_east_lga",
  },
  {
    id: "oluyole_estate",
    name: "Oluyole Estate",
    type: "neighborhood",
    parent: "ibadan_south_east_lga",
  },

  // Ibadan South West LGA
  {
    id: "challenge",
    name: "Challenge",
    type: "neighborhood",
    parent: "ibadan_south_west_lga",
  },
  {
    id: "dugbe",
    name: "Dugbe",
    type: "neighborhood",
    parent: "ibadan_south_west_lga",
  },
  {
    id: "oluyole_estate",
    name: "Oluyole Estate",
    type: "neighborhood",
    parent: "ibadan_south_west_lga",
  },
  {
    id: "ring_road",
    name: "Ring Road",
    type: "neighborhood",
    parent: "ibadan_south_west_lga",
  },

  // Akinyele LGA
  { id: "bola", name: "Bola", type: "neighborhood", parent: "akinyele_lga" },
  {
    id: "moniya",
    name: "Moniya",
    type: "neighborhood",
    parent: "akinyele_lga",
  },
  { id: "ojoo", name: "Ojoo", type: "neighborhood", parent: "akinyele_lga" },

  // Egbeda LGA
  {
    id: "egbeda_town",
    name: "Egbeda",
    type: "neighborhood",
    parent: "egbeda_lga",
  },
  { id: "alakia", name: "Alakia", type: "neighborhood", parent: "egbeda_lga" },

  // Oluyole LGA
  {
    id: "idi_ayunre",
    name: "Idi Ayunre",
    type: "neighborhood",
    parent: "oluyole_lga",
  },
  {
    id: "new_garage",
    name: "New Garage",
    type: "neighborhood",
    parent: "oluyole_lga",
  },

  // Lagelu LGA
  {
    id: "iyana_offa",
    name: "Iyana Offa",
    type: "neighborhood",
    parent: "lagelu_lga",
  },
  {
    id: "lalupon",
    name: "Lalupon",
    type: "neighborhood",
    parent: "lagelu_lga",
  },

  // Ogbomosho North LGA
  {
    id: "alaafin",
    name: "Alaafin",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },
  {
    id: "ogbomosho_central",
    name: "Ogbomosho Central",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },
  {
    id: "oja_ogun",
    name: "Oja Ogun",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },
  {
    id: "takie",
    name: "Takie",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },
  {
    id: "sabo",
    name: "Sabo",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },
  {
    id: "lautech_area",
    name: "LAUTECH Area",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },
  {
    id: "under_g",
    name: "Under G",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },
  {
    id: "jago",
    name: "Jago",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },
  {
    id: "oke_adini",
    name: "Oke Adini",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },
  {
    id: "aguodo",
    name: "Aguodo",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },
  {
    id: "bombata",
    name: "Bombata",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },
  {
    id: "arowomole_ext",
    name: "Arowomole Extension",
    type: "neighborhood",
    parent: "ogbomosho_north_lga",
  },

  // Ogbomosho South LGA
  {
    id: "arowomole",
    name: "Arowomole",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "iresaadu",
    name: "Iresaadu",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "ogbomosho_gra",
    name: "Ogbomosho GRA",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "oja_oba",
    name: "Oja-Oba",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "idi_igba",
    name: "Idi-Igba",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "gaa_lagbedu",
    name: "Gaa-Lagbedu",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "kajola",
    name: "Kajola",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "kowe",
    name: "Kowe",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "oke_ola",
    name: "Oke-Ola",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "adeoye",
    name: "Adeoye",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "onidewure",
    name: "Onidewure",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "molete",
    name: "Molete",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "sanuaje",
    name: "Sanuaje",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "obandi",
    name: "Obandi",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "ijeru",
    name: "Ijeru",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "ayegun",
    name: "Ayegun",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },
  {
    id: "oke_alapata",
    name: "Oke Alapata",
    type: "neighborhood",
    parent: "ogbomosho_south_lga",
  },

  // Oyo East LGA
  { id: "bara", name: "Bara", type: "neighborhood", parent: "oyo_east_lga" },
  {
    id: "aganju",
    name: "Aganju",
    type: "neighborhood",
    parent: "oyo_east_lga",
  },

  // Oyo West LGA
  { id: "awe", name: "Awe", type: "neighborhood", parent: "oyo_west_lga" },
  {
    id: "isokun",
    name: "Isokun",
    type: "neighborhood",
    parent: "oyo_west_lga",
  },

  // Iseyin LGA
  { id: "ajesa", name: "Ajesa", type: "neighborhood", parent: "iseyin_lga" },
  {
    id: "iseyin_town",
    name: "Iseyin Town",
    type: "neighborhood",
    parent: "iseyin_lga",
  },
  {
    id: "iseyin_central",
    name: "Iseyin Central",
    type: "neighborhood",
    parent: "iseyin_lga",
  },
  { id: "koso", name: "Koso", type: "neighborhood", parent: "iseyin_lga" },

  // Saki West LGA
  {
    id: "saki_town",
    name: "Saki Town",
    type: "neighborhood",
    parent: "saki_west_lga",
  },
  {
    id: "ago_are",
    name: "Ago Are",
    type: "neighborhood",
    parent: "saki_west_lga",
  },
];

/**
 * Get all neighborhoods for a specific LGA
 * @param lgaId The ID of the LGA to get neighborhoods for
 * @returns Array of neighborhood LocationData objects for the specified LGA
 */
export const getNeighborhoodsByLGA = (lgaId: string): LocationData[] => {
  // Check both Lagos and Oyo neighborhoods
  const lagosNeighborhoods = LAGOS_NEIGHBORHOODS.filter(
    (neighborhood) => neighborhood.parent === lgaId,
  );
  const oyoNeighborhoods = OYO_NEIGHBORHOODS.filter(
    (neighborhood) => neighborhood.parent === lgaId,
  );

  return [...lagosNeighborhoods, ...oyoNeighborhoods];
};

/**
 * Format location data into a readable address string
 * @param neighborhood Optional neighborhood name
 * @param lga Optional LGA name
 * @param state State name (defaults to Lagos)
 * @returns Formatted location string (e.g. "Lekki, Eti-Osa, Lagos, Nigeria")
 */
export const formatLocationString = (
  neighborhood?: string,
  lga?: string,
  state: string = "Lagos",
): string => {
  if (neighborhood && lga) {
    return `${neighborhood}, ${lga}, ${state}, Nigeria`;
  } else if (lga) {
    return `${lga}, ${state}, Nigeria`;
  }
  return `${state}, Nigeria`;
};

/**
 * Parse location string into component parts.
 * Supports Sell It Off hierarchical formats:
 * - "Neighborhood, LGA, State, Nigeria"
 * - "LGA, State, Nigeria"
 * - "State, Nigeria"
 * Legacy formats without "Nigeria" suffix are still accepted.
 */
export const parseLocationString = (
  locationString: string,
): {
  state?: string;
  lga?: string;
  neighborhood?: string;
} => {
  const trimmed = locationString.trim();
  if (!trimmed) {
    return {};
  }

  const parts = trimmed
    .split(", ")
    .map((p) => p.trim())
    .filter(Boolean);
  const n = parts.length;
  const endsNigeria = n > 0 && parts[n - 1] === "Nigeria";

  if (endsNigeria && n >= 4) {
    return {
      neighborhood: parts[0],
      lga: parts[1],
      state: parts[2],
    };
  }

  if (endsNigeria && n === 3) {
    return {
      lga: parts[0],
      state: parts[1],
    };
  }

  if (endsNigeria && n === 2) {
    return {
      state: parts[0],
    };
  }

  if (n >= 4) {
    return {
      neighborhood: parts[0],
      lga: parts[1],
      state: parts[2],
    };
  }

  if (n === 3) {
    return {
      neighborhood: parts[0],
      lga: parts[1],
      state: parts[2],
    };
  }

  if (n === 2) {
    return {
      lga: parts[0],
      state: parts[1],
    };
  }

  return {
    state: parts[0],
  };
};
